/**
 * Users, invitations, roles and permissions (M8, docs/dev/api-users.md): the German labels of the permission
 * matrix (Admin 01/02), the category grouping of the catalog, the value words, the labels of the users table
 * (Einstellungen 27), the wizard steps (Nutzer 02–12) and the formats of extension and mobile number (Nutzer 03,
 * Einstellungen 03). Server and client render with these; the server decides.
 */
/** Role columns of the matrix in the order of Admin 01 (the external role `developer_readonly` is not a user). */
export const matrixSystemRoleKeys = [
    'admin',
    'sales_lead',
    'advisor',
    'setter',
    'back_office',
    'financier',
    'referrer',
];
/** Row labels of Admin 01 (verbatim); keys the frame does not draw use the catalog label. */
export const permissionMatrixLabels = {
    'contacts.view': 'Kontakte sehen',
    'contacts.create': 'Kontakte anlegen',
    'contacts.export': 'Kontakte exportieren',
    'contacts.lift_dnc_merge': 'DNC aufheben, zusammenführen',
    'deals.view': 'Deals sehen',
    'deals.change_stage': 'Stage wechseln',
    'deals.lose_after_notarized_cancel': 'Verloren ab „Beurkundet", Storno',
    'deals.view_commission': 'Provision sehen',
    'financing.create': 'Anlegen',
    'financing.assign_financier': 'Finanzierer zuweisen',
    'properties.view': 'Objekte sehen',
    'properties.acquisition': 'In Ankauf sehen, anlegen',
    'properties.release': 'Freigeben, Provisionssatz setzen',
    'reservations.reserve': 'Reservieren',
    'reservations.approve': 'Reservierungen freigeben',
    'reservations.release_foreign': 'Fremde Reservierung aufheben',
    'tools.dialer': 'Dialer',
    'tools.tickets': 'Tickets',
    'tools.deals': 'Deals',
    'tools.financing': 'Finanzierungen',
    'tools.pkv': 'PKV',
    'controlling.figures': 'Zahlen',
    'settings.organization': 'Organisation, Nutzer, Rechte',
    'pkv.deals.view': 'PKV-Deals sehen',
    'pkv.deals.change_stage': 'PKV-Phase ändern',
    'pkv.health.view': 'Gesundheitsangaben sehen',
    'pkv.health.edit': 'Gesundheitsangaben bearbeiten',
    'pkv.risk_inquiry.view': 'Risikovoranfragen sehen',
    'pkv.risk_inquiry.process': 'Risikovoranfragen bearbeiten',
    'pkv.risk_inquiry.release': 'Ergebnisse freigeben',
    'pkv.calculator': 'PKV-Rechner',
    'pkv.application.generate': 'Antrag erzeugen',
    'pkv.commission.view': 'PKV-Provisionen sehen',
    'pkv.commission.book': 'PKV-Provisionen buchen',
};
/** Categories of the matrix (Admin 01) with every catalog key; `reservations.approve`, `tools.deals`, `tools.financing` join theirs. */
export const permissionCategories = [
    {
        label: 'Kontakte',
        keys: ['contacts.view', 'contacts.create', 'contacts.export', 'contacts.lift_dnc_merge'],
    },
    {
        label: 'Deals',
        keys: ['deals.view', 'deals.change_stage', 'deals.lose_after_notarized_cancel', 'deals.view_commission'],
    },
    { label: 'Finanzierungen', keys: ['financing.create', 'financing.assign_financier'] },
    { label: 'Objekte', keys: ['properties.view', 'properties.acquisition', 'properties.release'] },
    {
        label: 'Reservierung',
        keys: ['reservations.reserve', 'reservations.approve', 'reservations.release_foreign'],
    },
    {
        label: 'Tools',
        keys: ['tools.dialer', 'tools.tickets', 'tools.deals', 'tools.financing', 'tools.pkv'],
    },
    { label: 'Controlling', keys: ['controlling.figures'] },
    { label: 'Einstellungen', keys: ['settings.organization'] },
    {
        label: 'PKV',
        keys: [
            'pkv.deals.view',
            'pkv.deals.change_stage',
            'pkv.health.view',
            'pkv.health.edit',
            'pkv.risk_inquiry.view',
            'pkv.risk_inquiry.process',
            'pkv.risk_inquiry.release',
            'pkv.calculator',
            'pkv.application.generate',
            'pkv.commission.view',
            'pkv.commission.book',
        ],
    },
];
const yesNoWords = { yes: 'ja', no: 'nein' };
const noneYesWords = { yes: 'ja', none: 'nein' };
/** Value words of the matrix cells (Admin 01). */
export const permissionValueLabels = {
    'contacts.view': {
        all: 'alle',
        span: 'Führungsspanne',
        own_pool: 'eigene + Pool',
        pool_dialer: 'Pool, nur im Dialer',
        pool_cases: 'Pool und eigene Fälle',
        assigned_cases: 'zugewiesene Fälle',
        own_tips: 'eigene Tipps',
        none: 'nein',
    },
    'contacts.create': yesNoWords,
    'contacts.export': yesNoWords,
    'contacts.lift_dnc_merge': yesNoWords,
    'deals.view': {
        all: 'alle',
        span: 'Führungsspanne',
        own: 'eigene',
        assigned: 'zugewiesene',
        own_tips: 'eigene Tipps',
        none: 'nein',
    },
    'deals.change_stage': { yes: 'ja', own: 'eigene', none: 'nein' },
    'deals.lose_after_notarized_cancel': yesNoWords,
    'deals.view_commission': { all: 'alle', span: 'Führungsspanne', own: 'eigene', none: 'nein' },
    'financing.create': { yes: 'ja', if_assigned: 'wenn zugewiesen', none: 'nein' },
    'financing.assign_financier': { yes: 'ja', propose: 'Vorschlag', none: 'nein' },
    'properties.view': {
        all: 'alle',
        all_on_sale: 'alle im Vertrieb',
        on_sale: 'im Vertrieb',
        none: 'nein',
    },
    'properties.acquisition': yesNoWords,
    'properties.release': yesNoWords,
    'reservations.reserve': { yes: 'ja', request: 'Antrag', none: 'nein' },
    'reservations.approve': yesNoWords,
    'reservations.release_foreign': yesNoWords,
    'tools.dialer': yesNoWords,
    'tools.tickets': yesNoWords,
    'tools.deals': yesNoWords,
    'tools.financing': yesNoWords,
    'tools.pkv': yesNoWords,
    'controlling.figures': {
        organization: 'Organisation',
        span: 'Führungsspanne',
        own: 'eigene',
        tickets: 'Tickets',
        none: 'nein',
    },
    'settings.organization': { yes: 'ja', team: 'Team', none: 'nein' },
    'pkv.deals.view': {
        all: 'alle',
        span: 'Führungsspanne',
        own: 'eigene',
        assigned: 'zugewiesene',
        own_tips: 'eigene Tipps',
        none: 'nein',
    },
    'pkv.deals.change_stage': { yes: 'ja', own: 'eigene', none: 'nein' },
    'pkv.health.view': { full: 'vollständig', summary: 'nur Zusammenfassung', none: 'nein' },
    'pkv.health.edit': noneYesWords,
    'pkv.risk_inquiry.view': {
        all: 'alle',
        span: 'Führungsspanne',
        own_cases: 'eigene Fälle',
        none: 'nein',
    },
    'pkv.risk_inquiry.process': noneYesWords,
    'pkv.risk_inquiry.release': noneYesWords,
    'pkv.calculator': noneYesWords,
    'pkv.application.generate': noneYesWords,
    'pkv.commission.view': { all: 'alle', own: 'eigene', none: 'nein' },
    'pkv.commission.book': noneYesWords,
};
/** Every catalog key in matrix order (each appears in exactly one category). */
export const matrixPermissionKeys = permissionCategories.flatMap((c) => c.keys);
export function permissionValueLabel(key, value) {
    return permissionValueLabels[key][value] ?? value;
}
/** Roles a sales lead (`settings.organization = team`) may invite into his team (COMMON.md). */
export const teamInvitableRoleKeys = ['advisor', 'setter', 'referrer'];
/** Nutzer 01: „Stark · mindestens 12 Zeichen, Groß- und Kleinbuchstaben, eine Zahl". */
export const passwordRuleText = 'mindestens 12 Zeichen, Groß- und Kleinbuchstaben, eine Zahl';
/** Neutral row hint of Admin 10 for an address outside the organization's domains (Bauträger are not users). */
export const externalAddressHint = 'Externe Adresse';
/**
 * External memberships (0037, owner decision 2026-10-13): a member who works for another company inside the
 * organization (e.g. an agency) is labelled „extern · White Label Agency GmbH". A label only — the rights are those of
 * the role.
 */
export function externalMembershipLabel(company) {
    return `extern · ${company}`;
}
/** Field „Externe Firma" of the invitation and the edit dialog (D-228). */
export const externalCompanyFieldLabel = 'Externe Firma';
export const externalCompanyFieldHint = 'Mitglieder einer externen Firma (z. B. einer Agentur) arbeiten mit den Rechten ihrer Rolle in Ihrer Organisation. Die Angabe dient nur der Kennzeichnung.';
/** Steps of the user wizard in order (Nutzer 02–11 stepper). */
export const userSetupStepKeys = [
    'profile',
    'mailbox',
    'calendar',
    'availability',
    'whatsapp',
    'signature',
    'billing',
    'security',
];
export const userSetupStepLabels = {
    profile: 'Profil',
    mailbox: 'Postfach',
    calendar: 'Kalender',
    availability: 'Verfügbarkeit',
    whatsapp: 'WhatsApp',
    signature: 'Signatur',
    billing: 'Zahlungsmittel',
    security: 'Sicherheit',
};
/** Labels of the summary lines (Nutzer 12: „✓ Profil · … · Zwei-Faktor", „Offen: WhatsApp-Session, Zahlungsmittel …"). */
export const userSetupSummaryLabels = {
    profile: 'Profil',
    mailbox: 'Postfach',
    calendar: 'Kalender',
    availability: 'Verfügbarkeit',
    whatsapp: 'WhatsApp-Session',
    signature: 'Signatur',
    billing: 'Zahlungsmittel',
    security: 'Zwei-Faktor',
};
/** Users table (Einstellungen 27). */
export const userStatusLabels = {
    active: 'Aktiv',
    invited: 'Einladung offen',
    deactivated: 'Deaktiviert',
};
export const ownBillingLabels = {
    own: 'selbst zahlend',
    company: 'über Firma',
};
export const userRowActionLabels = {
    self: 'Sie selbst',
    edit: 'Bearbeiten',
    resend: 'Erneut senden',
    reactivate: 'Reaktivieren',
};
/**
 * Extension („Durchwahl") as stored: a hyphen and 1–5 digits (`-214`). Accepts `214`, `-214` and the full number
 * `+49 941 5920-231` (the part after the last hyphen). Null when nothing usable is left.
 */
export function normalizeExtension(input) {
    const trimmed = input.trim();
    if (trimmed === '')
        return null;
    const tail = trimmed.includes('-') ? trimmed.slice(trimmed.lastIndexOf('-') + 1) : trimmed;
    const digits = tail.replace(/\s/g, '');
    return /^\d{1,5}$/.test(digits) ? `-${digits}` : null;
}
/**
 * Mobile number in the „+49 …" format (Nutzer 03, Einstellungen 03): a leading `+`, digits and spaces only, 10 to
 * 15 digits. Returns the number with collapsed spaces or null.
 */
export function normalizeMobile(input) {
    const collapsed = input.trim().replace(/\s+/g, ' ');
    if (!/^\+[\d ]+$/.test(collapsed))
        return null;
    const digits = collapsed.replace(/\D/g, '');
    return digits.length >= 10 && digits.length <= 15 ? collapsed : null;
}
/** „T +49 941 5920-214": the organization's number followed by the extension. */
export function extensionPhone(organizationPhone, extension) {
    if (extension === null || extension === '')
        return organizationPhone;
    if (organizationPhone === null || organizationPhone.trim() === '')
        return null;
    return `${organizationPhone.trim()}${extension}`;
}
//# sourceMappingURL=users.js.map